import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react';

const PhiGuidelinesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="phi-guidelines-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              PHI-Zero Policy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-sm leading-relaxed text-gray-700">
            {/* Overview */}
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-2">
                What is PHI-Zero?
              </h2>
              <p>
                LitCurrent enforces a <strong>PHI-Zero</strong> policy: no
                Protected Health Information (PHI) may be posted, stored, or
                transmitted anywhere on the platform. This applies to all
                free-text inputs including discussion threads, comments, notes,
                and reports.
              </p>
            </section>

            {/* What counts */}
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <ShieldX className="h-4 w-4 text-red-500" />
                What is NOT allowed
              </h2>
              <ul className="list-disc ml-5 space-y-1">
                <li>Patient names or initials tied to clinical details</li>
                <li>Dates of birth (DOB)</li>
                <li>Social Security Numbers (SSN)</li>
                <li>Medical Record Numbers (MRN)</li>
                <li>Street addresses or specific locations</li>
                <li>Phone or fax numbers linked to individuals</li>
                <li>Insurance or policy IDs</li>
                <li>
                  Any combination of details that could identify a specific
                  patient
                </li>
              </ul>
            </section>

            {/* What is OK */}
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                What IS allowed
              </h2>
              <ul className="list-disc ml-5 space-y-1">
                <li>
                  De-identified clinical scenarios (e.g., "a 55-year-old male
                  with HFrEF")
                </li>
                <li>Published study data and citations</li>
                <li>General treatment protocols and guidelines</li>
                <li>Aggregate statistics and outcomes</li>
                <li>Your own clinical opinions and interpretations</li>
              </ul>
            </section>

            {/* Enforcement */}
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-2">
                How it works
              </h2>
              <p>
                Every piece of text you submit is scanned by our PHI detection
                system <em>before</em> it reaches the database. If potential PHI
                is detected, your submission will be rejected with a specific
                error message indicating which category was flagged. Simply
                remove the identified information and resubmit.
              </p>
              <p className="mt-2">
                This is a layered defense: the UI warns you, the backend blocks
                violations, and moderators provide a human backstop. Raw text is
                never logged — only user IDs and violation categories are
                recorded for audit purposes.
              </p>
            </section>

            {/* Tips */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-1.5">
                Quick tips for safe posting
              </h3>
              <ul className="list-disc ml-5 space-y-1 text-blue-800">
                <li>Use age ranges instead of exact dates of birth</li>
                <li>
                  Reference patients as "a patient" or "a 60-year-old female"
                </li>
                <li>Cite published literature rather than case-specific data</li>
                <li>
                  When discussing cases, change or omit all identifying details
                </li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PhiGuidelinesPage;
